import { Component, OnInit } from '@angular/core';
import { FeedbackSurveyService } from '../services/survey-tools.service';
import { SessionService } from 'src/app/core/backend-adapter/session.service';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { NotifyService } from 'src/app/core/layouts/notifications/notify/notify.service';
import { ReviewsService } from '../reviews.service';

@Component({
  selector: 'app-survey-tool',
  templateUrl: './survey-tool.component.html',
  styleUrls: ['./survey-tool.component.scss'],
})
export class SurveyToolComponent implements OnInit {
  private ngUnsubscribe$: Subject<any> = new Subject();
  private userLoginId: number;
  private accountId: number;
  private currentPageNumber: number;
  public currentUser: any;

  public actionItems = [
    {
      label: 'CSV',
      command: () => {
        this.exportCSV('csv');
      },
      disabled: false,
    },
    {
      label: 'XLSX',
      command: () => {
        this.exportXLSX('xlsx');
      },
      disabled: false,
    },
  ];
  // List of all feedback surveys.
  public feedbackSurveysList: any[] = [];
  // Main loading state of the page.
  public isLoadingData = false;
  public statisticsList: any[] = [];

  // Paginator properties
  public first: number = 0;
  public rows: number = 20;
  public totalRecords: number = 0;

  // Dialog properties
  public showEditDialog: boolean = false;
  public selectedSurvey: any = null;
  public editInternalComment: string = '';
  public editRespondedTo: boolean = false;
  public isSavingResponse: boolean = false;

  constructor(
    private feedbackSurveyService: FeedbackSurveyService,
    private sessionService: SessionService,
    private notifyService: NotifyService,
    private reviewsService: ReviewsService
  ) {}

  ngOnInit(): void {
    this.getCurrentAccountDetails();
    this.getCurrentUserDetails();
    // this.getFeedbackSurveysList();
  }

  /**
   * @description Method to get current account details.
   */
  private getCurrentAccountDetails(): void {
    this.sessionService.getSelectedAccount$().subscribe((account) => {
      this.accountId = account?._id || null;
    });
  }

  /**
   * @description Method to get feedback surveys list.
   */
  private getFeedbackSurveysList(page?: number, sortOrder: string = 'desc'): void {
    this.isLoadingData = true;
    this.feedbackSurveyService
      .getFeedbackSurveysList(this.accountId, this.userLoginId, page, sortOrder)
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe(
        (res) => {
          console.log('Raw data from getFeedbackSurveysList API:', res);
          const records = res?.records ?? res?.data?.records ?? [];
          if (records.length) {
            console.log('First raw record (keys from API):', records[0], 'Keys:', Object.keys(records[0]));
          }
          this.feedbackSurveysList = this.feedbackSurveyService.formatFeedbackSurveyData(records);
          console.log('Formatted feedbackSurveysList after processing:', this.feedbackSurveysList);
          this.totalRecords = res?.total ?? res?.data?.total ?? 0;
          this.statisticsList = res?.summary ?? res?.data?.summary ?? [];
          this.isLoadingData = false;
          this.notifyService.success('Feedback surveys fetched successfully.');
        },
        (err) => {
          this.isLoadingData = false;
          this.notifyService.info('Error while fetching feedback surveys.');
        },
        () => {
          this.isLoadingData = false;
        }
      );
  }

  /**
   * @description Method to export feedback surveys in CSV format.
   * @param format csv format to export feedback surveys.
   */
  exportCSV(format: string): void {
    this.reviewsService.genCSVReport(this.feedbackSurveysList, format, 'feedback-survey');
  }

  /**
   * @description Method to export feedback surveys in XLSX format.
   * @param format xlsx format to export feedback surveys.
   */
  exportXLSX(format: string): void {
    const headers = Object.keys(this.feedbackSurveysList[0]);
    const tableData = this.feedbackSurveysList.map((survey) => {
      return Object.values(survey);
    });
    const xlslDataArray = [headers, ...tableData];
    this.reviewsService.exportAsExcelFile(xlslDataArray, format);
  }

  /**
   * @description Method to handle page change event from paginator.
   * @param event Page change event from paginator
   */
  public pageChange(event): void {
    this.first = event.first;
    this.rows = event.rows;
  }

  public lazyLoadLocations(event) {
    let sortOrder: string = 'desc';
    if (!event.multiSortMeta) {
      sortOrder = 'desc';
    } else {
      if (event.multiSortMeta[0].order === 1) {
        sortOrder = 'asc';
      } else if (event.multiSortMeta[0].order === -1) {
        sortOrder = 'desc';
      } else {
        sortOrder = 'desc';
      }
    }
    this.currentPageNumber = event.first / 100 + 1;
    this.getFeedbackSurveysList(this.currentPageNumber, sortOrder);
  }

  /**
   * @description: Get the current user details.
   * @returns: void
   * @arguments: void
   */
  private getCurrentUserDetails(): void {
    this.sessionService
      .getCurrentUser$()
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((user) => {
        this.userLoginId = user?.login?._id;
        this.currentUser = user; // Store user for "Responded by" field
      });
  }

  /**
   * @description Open edit dialog for response fields
   * @param survey Survey data to edit
   */
  public openEditDialog(survey: any): void {
    this.selectedSurvey = survey;
    this.editInternalComment = survey?.internal_user_comment || '';
    this.editRespondedTo = survey?.responded_to ?? false;
    this.showEditDialog = true;
  }

  /**
   * @description Close edit dialog without saving
   */
  public closeEditDialog(): void {
    this.showEditDialog = false;
    this.selectedSurvey = null;
    this.editInternalComment = '';
    this.editRespondedTo = false;
  }

  /**
   * @description Build "Responded by" object for API: { user_id, name, email_id }.
   */
  public getRespondedByValue(): { user_id: number; name: string; email_id: string } | null {
    if (!this.currentUser?.login) {
      return null;
    }
    const user_id = this.currentUser.login._id;
    const name = this.currentUser.login.note ?? this.currentUser.login.name ?? '';
    const email_id = this.currentUser.login.loginId ?? this.currentUser.email_id ?? '';
    return { user_id, name, email_id };
  }

  /**
   * @description Display string for "Responded by" in UI (e.g. "rory (rory@renderseo.com)").
   */
  public getRespondedByDisplayText(): string {
    const obj = this.getRespondedByValue();
    if (!obj) {
      return 'N/A';
    }
    return obj.email_id ? `${obj.name} (${obj.email_id})` : obj.name || 'N/A';
  }

  /**
   * @description Save edited response fields via survey update API.
   */
  public saveResponseFields(): void {
    if (!this.selectedSurvey) {
      return;
    }
    const accountIdent = this.selectedSurvey.account_ident;
    const type = this.selectedSurvey.type;
    if (!accountIdent || !type) {
      this.notifyService.info('Missing account or response type; cannot update.');
      return;
    }
    const respondedBy = this.getRespondedByValue();
    const body = {
      account_ident: accountIdent,
      type,
      updates: {
        responded_to: this.editRespondedTo,
        internal_user_comment: this.editInternalComment,
        ...(respondedBy && { responded_by: respondedBy }),
      },
    };
    console.log('Survey update API request body:', body);
    this.isSavingResponse = true;
    this.feedbackSurveyService
      .updateSurveyResponse(this.accountId, this.userLoginId, body)
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe(
        () => {
          this.selectedSurvey.responded_to = this.editRespondedTo;
          this.selectedSurvey.internal_user_comment = this.editInternalComment;
          this.selectedSurvey.responded_by = this.getRespondedByDisplayText();
          this.notifyService.success('Response updated successfully.');
          this.closeEditDialog();
        },
        () => {
          this.notifyService.info('Error while updating response.');
        },
        () => {
          this.isSavingResponse = false;
        }
      );
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }
}
